/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import api from '../../services/axios'
import jsPDF from 'jspdf'
import GidIcon from '../../assets/icons/gid-logo.png'
import autoTable from 'jspdf-autotable'
import { CiSaveDown1 } from 'react-icons/ci'
import { DashMain } from '../SomarizadorScreen/styles'
import Table, { TableColumn, Row } from './Table'
import GeralChartCard from './GeralChartCard'
import { useGeralFilter } from '../../context/FiltersContext/PeriodoFilter'
import { Skeleton } from '../../components/Skeletron'
import { normalizeDateForComparison } from '../../constantes'
import { carregarDistribuidores } from '../../services/dists'
import { IoStatsChartOutline, IoGridOutline, IoLayersOutline } from 'react-icons/io5'

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

const getBase64ImageFromUrl = async (imageUrl: string) => {
  try {
    const res = await fetch(imageUrl)
    const blob = await res.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Erro ao converter logo:', error)
    return imageUrl
  }
}

const sumArray = (arr: number[]) => arr.reduce((a, b) => a + b, 0)
const ensureArray = (data: any) => (Array.isArray(data) ? data : [])

const REFRESH_INTERVAL = 300000

export type ViewFormat = 'tabelas' | 'graficos' | 'ambos'

export default function GeralScreen() {
  const {
    month,
    compareMonth,
    endMonth,
    year,
    compareYear,
    distributorId: filterDistId,
    distributorIds,
    startDate,
    endDate,
    categoria,
    marca,
    operacao,
    gestor,
    visao,
    semana,
    semanas,
    trimestre,
    trimestres,
    mesesAno,
  } = useGeralFilter()

  const activeAnoMonths = useMemo(() => {
    return Array.isArray(mesesAno)
      ? [...mesesAno].sort((a, b) => a - b)
      : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
  }, [mesesAno])

  const [selloutAgregado, setSelloutAgregado] = useState<any[]>([])
  const [metas, setMetas] = useState<any[]>([])
  const [estoqueAgregado, setEstoqueAgregado] = useState<any[]>([])
  const [distribuidores, setDistribuidores] = useState<any[]>([])
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)

  // Estado de Visualização: Tabelas como padrão inicial
  const [viewFormat, setViewFormat] = useState<ViewFormat>('tabelas')

  const dashRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    load()

    const intervalId = setInterval(() => {
      load()
    }, REFRESH_INTERVAL)

    return () => clearInterval(intervalId)
  }, [
    filterDistId,
    distributorIds,
    categoria,
    marca,
    operacao,
    gestor,
    year,
    compareYear,
    month,
    compareMonth,
    endMonth,
    startDate,
    endDate,
    visao,
    semana,
    semanas,
    trimestre,
    trimestres,
  ])

  const load = async () => {
    setLoading(true)
    try {
      const distIdsParam =
        distributorIds && distributorIds.length > 0
          ? distributorIds.join(',')
          : filterDistId || null

      const params = {
        distributorId: distIdsParam,
        distributorIds: distIdsParam,
        categoria,
        marca,
        operacao,
        gestor,
        startDate: startDate ? normalizeDateForComparison(startDate) : null,
        endDate: endDate ? normalizeDateForComparison(endDate) : null,
        year,
        visao,
      }

      const shouldFetchEstoque = visao === 'ano' || visao === 'mes'

      const promises: Promise<any>[] = [
        carregarDistribuidores(),
        api.get('/get/sellout/geral-agregado', { params }),
        api.get('/get/metas', { params }),
      ]

      if (shouldFetchEstoque) {
        promises.push(api.get('/get/estoque/geral-agregado', { params }))
      }

      const results = await Promise.all(promises)

      const dists = results[0]
      const soAgregado = results[1]
      const resMetas = results[2]
      const estAgregado = shouldFetchEstoque ? results[3] : { data: [] }

      setDistribuidores(dists)
      setSelloutAgregado(ensureArray(soAgregado.data))
      setMetas(ensureArray(resMetas.data))
      setEstoqueAgregado(ensureArray(estAgregado?.data || []))

      setLastUpdate(new Date())
    } catch (error) {
      console.error('Erro:', error)
    } finally {
      setLoading(false)
    }
  }

  const getFormattedDate = () => {
    if (!lastUpdate) return ''
    const d = lastUpdate.getDate().toString().padStart(2, '0')
    const m = (lastUpdate.getMonth() + 1).toString().padStart(2, '0')
    const y = lastUpdate.getFullYear()
    const h = lastUpdate.getHours().toString().padStart(2, '0')
    const min = lastUpdate.getMinutes().toString().padStart(2, '0')

    return `última atualização ${d}/${m}/${y} ${h}:${min}h`
  }

  const targetCompare = useMemo(() => {
    return compareYear && compareYear < year ? compareYear : year - 1
  }, [year, compareYear])

  const yearsToCompare = useMemo(() => {
    return [targetCompare, year]
  }, [year, targetCompare])

  // Mês alvo para visões de Mês
  const activeMonthIdx = useMemo(() => {
    return month === -1 ? new Date().getMonth() : month
  }, [month])

  const activeCompareMonthIdx = useMemo(() => {
    return compareMonth !== undefined && compareMonth !== -1 ? compareMonth : activeMonthIdx
  }, [compareMonth, activeMonthIdx])

  // Lista de meses selecionados na Visão de Semanas (suporta múltiplos meses)
  const selectedMonths = useMemo(() => {
    if (visao !== 'semanas') return []
    const start = month === -1 ? new Date().getMonth() : month
    const end = endMonth === -1 ? start : endMonth
    const minM = Math.min(start, end)
    const maxM = Math.max(start, end)
    const months: number[] = []
    for (let m = minM; m <= maxM; m++) {
      months.push(m)
    }
    return months
  }, [visao, month, endMonth])

  // Helper para calcular as semanas reversas de um determinado mês
  const getWeeklyBoundariesForMonth = (targetYear: number, mIdx: number) => {
    const ultimoDia = new Date(targetYear, mIdx + 1, 0).getDate()
    const diaS4Inicio = Math.max(1, ultimoDia - 6)
    const diaS3Inicio = Math.max(1, ultimoDia - 13)
    const diaS2Inicio = Math.max(1, ultimoDia - 20)
    return { ultimoDia, diaS4Inicio, diaS3Inicio, diaS2Inicio }
  }

  // Colunas da Tabela conforme Modo de Visão
  const tableColumns = useMemo((): TableColumn[] => {
    if (visao === 'trimestre') {
      const quarters = [
        { key: 'Q1', label: '1º TRIM (Q1)', sublabel: 'Jan - Mar' },
        { key: 'Q2', label: '2º TRIM (Q2)', sublabel: 'Abr - Jun' },
        { key: 'Q3', label: '3º TRIM (Q3)', sublabel: 'Jul - Set' },
        { key: 'Q4', label: '4º TRIM (Q4)', sublabel: 'Out - Dez' },
      ]
      const parsedTrimestres =
        trimestres && trimestres.length > 0
          ? trimestres
          : trimestre && trimestre !== 'todos'
            ? trimestre.split(',').filter(Boolean)
            : []
      if (parsedTrimestres.length > 0) {
        return quarters
          .filter((q) => parsedTrimestres.includes(q.key))
          .map(({ label, sublabel }) => ({ label, sublabel }))
      }
      return quarters.map(({ label, sublabel }) => ({ label, sublabel }))
    }

    if (visao === 'mes') {
      if (activeMonthIdx === activeCompareMonthIdx) {
        return [
          {
            label: monthNames[activeMonthIdx].toUpperCase(),
            sublabel: `${year} vs ${targetCompare}`,
          },
        ]
      } else {
        return [
          {
            label: `${monthNames[activeMonthIdx].slice(0, 3).toUpperCase()} (${year})`,
            sublabel: `vs ${monthNames[activeCompareMonthIdx].slice(0, 3).toUpperCase()} (${targetCompare})`,
          },
        ]
      }
    }

    if (visao === 'semanas') {
      const isMulti = selectedMonths.length > 1
      const parsedSemanas =
        semanas && semanas.length > 0
          ? semanas
          : semana && semana !== 'todas'
            ? semana.split(',').filter(Boolean)
            : []

      return selectedMonths.flatMap((m) => {
        const { ultimoDia, diaS4Inicio, diaS3Inicio, diaS2Inicio } = getWeeklyBoundariesForMonth(year, m)
        const mPrefix = monthNames[m].slice(0, 3).toUpperCase()
        const weeks = [
          { key: 'S1', label: isMulti ? `${mPrefix} S1` : 'Semana 1 (S1)', sublabel: `01 a ${diaS2Inicio - 1}` },
          { key: 'S2', label: isMulti ? `${mPrefix} S2` : 'Semana 2 (S2)', sublabel: `${diaS2Inicio} a ${diaS3Inicio - 1}` },
          { key: 'S3', label: isMulti ? `${mPrefix} S3` : 'Semana 3 (S3)', sublabel: `${diaS3Inicio} a ${diaS4Inicio - 1}` },
          { key: 'S4', label: isMulti ? `${mPrefix} S4` : 'Semana 4 (S4)', sublabel: `${diaS4Inicio} a ${ultimoDia}` },
        ]

        if (parsedSemanas.length > 0) {
          return weeks.filter((w) => parsedSemanas.includes(w.key)).map(({ label, sublabel }) => ({ label, sublabel }))
        }
        return weeks.map(({ label, sublabel }) => ({ label, sublabel }))
      })
    }

    // Visão Padrão: Ano vs Ano (meses selecionados)
    return activeAnoMonths.map((m) => ({ label: monthNames[m].slice(0, 3).toUpperCase() }))
  }, [
    visao,
    semana,
    semanas,
    trimestre,
    trimestres,
    activeMonthIdx,
    activeCompareMonthIdx,
    targetCompare,
    year,
    selectedMonths,
    activeAnoMonths,
  ])

  // --- CÁLCULO DE SELLOUT EM CAIXAS (12 MESES) ---
  const calcSelloutAnoPorMes = (targetYear: number) => {
    const values = Array(12).fill(0)
    selloutAgregado.forEach((item) => {
      const itemYear = Number(item.ano)
      const itemMonth = Number(item.mes) - 1
      if (itemYear === targetYear && itemMonth >= 0 && itemMonth < 12) {
        values[itemMonth] += Number(item.total_caixas || 0)
      }
    })
    return values
  }

  // --- CÁLCULO DE SELLOUT EM VALOR (12 MESES) ---
  const calcSelloutValorAnoPorMes = (targetYear: number) => {
    const values = Array(12).fill(0)
    selloutAgregado.forEach((item) => {
      const itemYear = Number(item.ano)
      const itemMonth = Number(item.mes) - 1
      if (
        itemYear === targetYear &&
        itemMonth >= 0 &&
        itemMonth < 12 &&
        String(item.tipo_venda || '').trim().toUpperCase() === 'N'
      ) {
        values[itemMonth] += Number(item.total_valor || 0)
      }
    })
    return values
  }

  // --- CÁLCULO DE COBERTURA (12 MESES) ---
  const calcCoberturaAnoPorMes = (targetYear: number) => {
    const values = Array(12).fill(0)
    selloutAgregado.forEach((item) => {
      const itemYear = Number(item.ano)
      const itemMonth = Number(item.mes) - 1
      if (
        itemYear === targetYear &&
        itemMonth >= 0 &&
        itemMonth < 12 &&
        String(item.tipo_venda || '').trim().toUpperCase() === 'N'
      ) {
        values[itemMonth] += Number(item.cobertura || 0)
      }
    })
    return values
  }

  // --- CÁLCULO DE BONIFICAÇÃO (12 MESES) ---
  const calcBonificacaoAnoPorMes = (targetYear: number) => {
    const values = Array(12).fill(0)
    selloutAgregado.forEach((item) => {
      const itemYear = Number(item.ano)
      const itemMonth = Number(item.mes) - 1
      if (
        itemYear === targetYear &&
        itemMonth >= 0 &&
        itemMonth < 12 &&
        String(item.tipo_venda || '').trim().toUpperCase() === 'B'
      ) {
        values[itemMonth] += Number(item.total_valor || 0)
      }
    })
    return values
  }

  // --- CÁLCULO DE ESTOQUE (12 MESES) ---
  const calcEstoqueAnoPorMes = (targetYear: number) => {
    const values = Array(12).fill(0)
    estoqueAgregado.forEach((item) => {
      const itemYear = Number(item.ano)
      const itemMonth = Number(item.mes) - 1
      if (itemYear === targetYear && itemMonth >= 0 && itemMonth < 12) {
        values[itemMonth] += Number(item.total_caixas || 0)
      }
    })
    return values
  }

  // --- CÁLCULO SEMANAL S1-S4 PARA UM ANO ESPECÍFICO ---
  const calcWeeklyData = (targetYear: number, type: 'cx' | 'vl' | 'cob' | 'bonif') => {
    const allWeeks: number[] = []

    selectedMonths.forEach((mIdx) => {
      const weekValues = [0, 0, 0, 0]

      selloutAgregado.forEach((item) => {
        const itemYear = Number(item.ano)
        const itemMonth = Number(item.mes) - 1
        const itemSemana = String(item.semana || '').toUpperCase()

        if (itemYear === targetYear && itemMonth === mIdx) {
          let sIdx = -1
          if (itemSemana === 'S1') sIdx = 0
          else if (itemSemana === 'S2') sIdx = 1
          else if (itemSemana === 'S3') sIdx = 2
          else if (itemSemana === 'S4') sIdx = 3

          if (sIdx >= 0) {
            const tipoVenda = String(item.tipo_venda || '').trim().toUpperCase()
            if (type === 'cx') {
              weekValues[sIdx] += Number(item.total_caixas || 0)
            } else if (type === 'vl' && tipoVenda === 'N') {
              weekValues[sIdx] += Number(item.total_valor || 0)
            } else if (type === 'cob' && tipoVenda === 'N') {
              weekValues[sIdx] += Number(item.cobertura || 0)
            } else if (type === 'bonif' && tipoVenda === 'B') {
              weekValues[sIdx] += Number(item.total_valor || 0)
            }
          }
        }
      })

      const parsedSemanas =
        semanas && semanas.length > 0
          ? semanas
          : semana && semana !== 'todas'
            ? semana.split(',').filter(Boolean)
            : []

      if (parsedSemanas.length > 0) {
        parsedSemanas.forEach((s) => {
          const targetIdx = s === 'S1' ? 0 : s === 'S2' ? 1 : s === 'S3' ? 2 : s === 'S4' ? 3 : -1
          if (targetIdx >= 0) {
            allWeeks.push(weekValues[targetIdx])
          }
        })
      } else {
        allWeeks.push(...weekValues)
      }
    })

    return allWeeks
  }

  // --- OBTENÇÃO DE METAS ---
  const getMetaForYear = (targetYear: number, type: 'cx' | 'vl') => {
    const monthlyMeta = Array(12).fill(0)
    metas.forEach((m) => {
      if (!m.data) return
      const dateStr = String(m.data).split('T')[0]
      const parts = dateStr.split('-')
      if (parts.length >= 2) {
        const y = parseInt(parts[0], 10)
        const mo = parseInt(parts[1], 10)
        if (y === targetYear) {
          const val = type === 'cx' ? m.meta_sellin_cx : m.meta_sellin_vl
          monthlyMeta[mo - 1] += Number(val || 0)
        }
      }
    })
    return monthlyMeta
  }

  // --- TRANSFORMAÇÃO DE 12 MESES PARA O MODO DE VISÃO ATIVO ---
  const transformValuesToVisao = (
    monthlyValues: number[],
    targetYear: number,
    type: 'cx' | 'vl' | 'cob' | 'bonif',
  ) => {
    if (visao === 'trimestre') {
      const q1 = sumArray(monthlyValues.slice(0, 3))
      const q2 = sumArray(monthlyValues.slice(3, 6))
      const q3 = sumArray(monthlyValues.slice(6, 9))
      const q4 = sumArray(monthlyValues.slice(9, 12))
      const quarters = [
        { key: 'Q1', val: q1 },
        { key: 'Q2', val: q2 },
        { key: 'Q3', val: q3 },
        { key: 'Q4', val: q4 },
      ]
      const parsedTrimestres =
        trimestres && trimestres.length > 0
          ? trimestres
          : trimestre && trimestre !== 'todos'
            ? trimestre.split(',').filter(Boolean)
            : []
      if (parsedTrimestres.length > 0) {
        return quarters
          .filter((q) => parsedTrimestres.includes(q.key))
          .map((q) => q.val)
      }
      return [q1, q2, q3, q4]
    }

    if (visao === 'mes') {
      if (targetYear === targetCompare) {
        return [monthlyValues[activeCompareMonthIdx] || 0]
      }
      return [monthlyValues[activeMonthIdx] || 0]
    }

    if (visao === 'semanas') {
      return calcWeeklyData(targetYear, type)
    }

    // Modo Ano vs Ano (meses selecionados)
    return activeAnoMonths.map((m) => monthlyValues[m] || 0)
  }

  // --- CONSTRUTOR GENÉRICO DE LINHAS COM METAS, PERCENTUAIS E CRESCIMENTO VS ---
  const buildRowsWithMeta = (
    baseRows: Row[],
    type: 'cx' | 'vl',
    metaLabel: string,
    isSellout = false,
  ): Row[] => {
    const rawMetaAtual = getMetaForYear(year, type)
    const metaAtualMonthly = rawMetaAtual.map((v) => {
      const val = isSellout ? v * 1.05 : v
      return type === 'cx' ? Math.round(val) : val
    })

    let filteredMetaAtual: number[] = []

    if (visao === 'trimestre') {
      filteredMetaAtual = [
        sumArray(metaAtualMonthly.slice(0, 3)),
        sumArray(metaAtualMonthly.slice(3, 6)),
        sumArray(metaAtualMonthly.slice(6, 9)),
        sumArray(metaAtualMonthly.slice(9, 12)),
      ]
    } else if (visao === 'mes') {
      filteredMetaAtual = [metaAtualMonthly[activeMonthIdx] || 0]
    } else if (visao === 'semanas') {
      filteredMetaAtual = selectedMonths.flatMap((m) => {
        const metaMes = metaAtualMonthly[m] || 0
        const metaSemana = metaMes / 4
        if (semana && semana !== 'todas') {
          return [metaSemana]
        }
        return [metaSemana, metaSemana, metaSemana, metaSemana]
      })
    } else {
      filteredMetaAtual = activeAnoMonths.map((m) => metaAtualMonthly[m] || 0)
    }

    const metaTotal =
      type === 'cx' ? Math.round(sumArray(filteredMetaAtual)) : sumArray(filteredMetaAtual)

    baseRows.push({
      label: metaLabel,
      values: filteredMetaAtual,
      total: metaTotal,
      highlight: '#fff7d6',
    })

    const rowAnoAtual = baseRows.find(
      (r) =>
        r.label &&
        r.label.includes(String(year)) &&
        !r.label.includes('vs') &&
        !r.label.includes('Meta'),
    )

    if (rowAnoAtual) {
      const percValues = rowAnoAtual.values.map((v: number, i: number) => {
        const m = filteredMetaAtual[i] || 0
        if (m === 0) return 0
        return (v / m - 1) * 100
      })

      const percTotal = metaTotal === 0 ? 0 : (rowAnoAtual.total / metaTotal - 1) * 100

      baseRows.push({
        label: 'Meta %',
        values: percValues,
        total: percTotal,
        isPercentage: true,
        highlight: '#e6f7ff',
      })
    }

    const rowAnoComparado = baseRows.find(
      (r) =>
        r.label &&
        r.label.includes(String(targetCompare)) &&
        !r.label.includes('vs') &&
        !r.label.includes('Meta'),
    )

    if (rowAnoAtual && rowAnoComparado) {
      const growthValues = rowAnoAtual.values.map((v: number, i: number) => {
        const prev = rowAnoComparado.values[i] || 0
        if (prev === 0) return 0
        return (v / prev - 1) * 100
      })

      const growthTotal =
        rowAnoComparado.total === 0 ? 0 : (rowAnoAtual.total / rowAnoComparado.total - 1) * 100

      const vsLabel =
        visao === 'mes' && activeMonthIdx !== activeCompareMonthIdx
          ? `${monthNames[activeMonthIdx].slice(0, 3)}/${year} vs ${monthNames[activeCompareMonthIdx].slice(0, 3)}/${targetCompare}`
          : `${year} vs ${targetCompare}`

      baseRows.push({
        label: vsLabel,
        values: growthValues,
        total: growthTotal,
        isPercentage: true,
        highlight: '#f0f5ff',
      })
    }

    return baseRows
  }

  // --- LINHAS DE SELLOUT EM CAIXAS ---
  const selloutRows = useMemo((): Row[] => {
    const base: Row[] = yearsToCompare.map((y) => {
      const monthly = calcSelloutAnoPorMes(y)
      const values = transformValuesToVisao(monthly, y, 'cx').map((v) => Math.round(v))
      const total = Math.round(sumArray(values))

      const label =
        visao === 'mes' && y === targetCompare && activeMonthIdx !== activeCompareMonthIdx
          ? `Sellout ${monthNames[activeCompareMonthIdx].slice(0, 3)} ${y}`
          : `Sellout ${y}`

      return {
        label,
        values,
        total,
        highlight: y === year ? '#dfffe0' : undefined,
      }
    })

    return buildRowsWithMeta(base, 'cx', 'Meta', true)
  }, [
    yearsToCompare,
    month,
    compareMonth,
    endMonth,
    year,
    targetCompare,
    selloutAgregado,
    metas,
    visao,
    semana,
    activeMonthIdx,
    activeCompareMonthIdx,
    selectedMonths,
    activeAnoMonths,
  ])

  // --- LINHAS DE SELLOUT EM VALOR ---
  const selloutValorRows = useMemo((): Row[] => {
    const base: Row[] = yearsToCompare.map((y) => {
      const monthly = calcSelloutValorAnoPorMes(y)
      const values = transformValuesToVisao(monthly, y, 'vl')
      const total = sumArray(values)

      const label =
        visao === 'mes' && y === targetCompare && activeMonthIdx !== activeCompareMonthIdx
          ? `Sellout R$ ${monthNames[activeCompareMonthIdx].slice(0, 3)} ${y}`
          : `Sellout R$ ${y}`

      return {
        label,
        values,
        total,
        highlight: y === year ? '#e6f7ff' : undefined,
      }
    })

    return buildRowsWithMeta(base, 'vl', 'Meta', true)
  }, [
    yearsToCompare,
    month,
    compareMonth,
    endMonth,
    year,
    targetCompare,
    selloutAgregado,
    metas,
    visao,
    semana,
    activeMonthIdx,
    activeCompareMonthIdx,
    selectedMonths,
    activeAnoMonths,
  ])

  // --- LINHAS DE SELLIN EM VALOR ---
  const sellinValorRows = useMemo((): Row[] => {
    const base: Row[] = yearsToCompare.map((y) => {
      const emptyMonthly = Array(12).fill(0)
      const values = transformValuesToVisao(emptyMonthly, y, 'vl')
      return {
        label: `Sellin R$ ${y}`,
        values,
        total: 0,
        highlight: y === year ? '#e6f7ff' : undefined,
      }
    })

    return buildRowsWithMeta(base, 'vl', 'Meta')
  }, [
    yearsToCompare,
    month,
    compareMonth,
    endMonth,
    year,
    targetCompare,
    metas,
    visao,
    semana,
    activeMonthIdx,
    activeCompareMonthIdx,
    selectedMonths,
    activeAnoMonths,
  ])

  // --- LINHAS DE COBERTURA COM META DA COBERTURA ---
  const coberturaRows = useMemo((): Row[] => {
    const base: Row[] = yearsToCompare.map((y) => {
      const monthly = calcCoberturaAnoPorMes(y)
      const values = transformValuesToVisao(monthly, y, 'cob')
      const total = sumArray(values)
      return {
        label: `Cob ${y}`,
        values,
        total,
        highlight: y === year ? '#ffe2bb' : undefined,
      }
    })

    const rowAnoAtual = base.find((r) => r.label.includes(String(year)))
    const rowAnoComparado = base.find((r) => r.label.includes(String(targetCompare)))

    if (rowAnoAtual) {
      const metaCobValues = (rowAnoComparado ? rowAnoComparado.values : rowAnoAtual.values).map(
        (v) => Math.round(v * 1.05 || 100),
      )
      const metaCobTotal = sumArray(metaCobValues)

      base.push({
        label: 'Meta Cobertura',
        values: metaCobValues,
        total: metaCobTotal,
        highlight: '#fff7d6',
      })

      const percCobValues = rowAnoAtual.values.map((v, i) => {
        const m = metaCobValues[i] || 0
        if (m === 0) return 0
        return (v / m - 1) * 100
      })
      const percCobTotal =
        metaCobTotal === 0 ? 0 : (rowAnoAtual.total / metaCobTotal - 1) * 100

      base.push({
        label: 'Meta %',
        values: percCobValues,
        total: percCobTotal,
        isPercentage: true,
        highlight: '#e6f7ff',
      })
    }

    return base
  }, [
    yearsToCompare,
    month,
    compareMonth,
    endMonth,
    year,
    targetCompare,
    selloutAgregado,
    visao,
    semana,
    activeMonthIdx,
    activeCompareMonthIdx,
    selectedMonths,
    activeAnoMonths,
  ])

  // --- LINHAS DE BONIFICAÇÃO ---
  const bonificacaoRows = useMemo((): Row[] => {
    const base: Row[] = yearsToCompare.map((y) => {
      const monthly = calcBonificacaoAnoPorMes(y)
      const values = transformValuesToVisao(monthly, y, 'bonif')
      const total = sumArray(values)
      return {
        label: `Bonif R$ ${y}`,
        values,
        total,
        highlight: y === year ? '#e6f7ff' : undefined,
      }
    })
    return base
  }, [
    yearsToCompare,
    month,
    compareMonth,
    endMonth,
    year,
    targetCompare,
    selloutAgregado,
    visao,
    semana,
    activeMonthIdx,
    activeCompareMonthIdx,
    selectedMonths,
    activeAnoMonths,
  ])

  // --- LINHAS DE ESTOQUE (APENAS ANO VS ANO E MÊS VS MÊS) ---
  const estoqueRows = useMemo((): Row[] => {
    if (visao === 'trimestre' || visao === 'semanas') return []

    const base: Row[] = yearsToCompare.map((y) => {
      const monthly = calcEstoqueAnoPorMes(y)
      const values = transformValuesToVisao(monthly, y, 'cx').map((v) => Math.round(v))
      const total = Math.round(sumArray(values))

      return {
        label: `Estoque ${y}`,
        values,
        total,
        highlight: y === year ? '#ffe2bb' : undefined,
      }
    })

    const result: Row[] = [...base]

    const estoqueMesAnoAtual = calcEstoqueAnoPorMes(year)
    const coberturaMesAnoAtual = calcCoberturaAnoPorMes(year)

    const diasValuesMonthly = estoqueMesAnoAtual.map((est, i) => {
      const cob = coberturaMesAnoAtual[i] || 0
      if (cob === 0) return 0
      return Math.round(est / cob)
    })

    const diasValues = transformValuesToVisao(diasValuesMonthly, year, 'cx').map((v) =>
      Math.round(v),
    )
    const diasTotal = Math.round(sumArray(diasValues))

    result.push({
      label: 'Dias Estoque',
      values: diasValues,
      total: diasTotal,
      highlight: '#fff7d6',
    })

    return result
  }, [
    yearsToCompare,
    month,
    compareMonth,
    endMonth,
    year,
    targetCompare,
    estoqueAgregado,
    selloutAgregado,
    visao,
    activeMonthIdx,
    activeCompareMonthIdx,
    activeAnoMonths,
  ])

  // LISTA DE TODAS AS MÉTRICAS (TODAS RENDERIZADAS NA MESMA TELA)
  const allMetrics = useMemo(() => {
    const list: Array<{
      id: string
      title: string
      color: string
      chartColor: string
      rows: Row[]
      isCurrency: boolean
      unit: string
    }> = [
      {
        id: 'sellout_cx',
        title: 'REAL SELLOUT (CX)',
        color: '#0b6b2c',
        chartColor: '#00E396',
        rows: selloutRows,
        isCurrency: false,
        unit: 'cx',
      },
      {
        id: 'sellout_vl',
        title: 'REAL SELLOUT (R$)',
        color: '#0050b3',
        chartColor: '#5470c6',
        rows: selloutValorRows,
        isCurrency: true,
        unit: '',
      },
      {
        id: 'sellin_vl',
        title: 'REAL SELLIN (R$)',
        color: '#034668',
        chartColor: '#008FFB',
        rows: sellinValorRows,
        isCurrency: true,
        unit: '',
      },
      {
        id: 'bonif_vl',
        title: 'REAL BONIFICAÇÃO (R$)',
        color: '#722ed1',
        chartColor: '#722ed1',
        rows: bonificacaoRows,
        isCurrency: true,
        unit: '',
      },
      {
        id: 'cobertura',
        title: 'REAL COBERTURA',
        color: '#d46b00',
        chartColor: '#FEB019',
        rows: coberturaRows,
        isCurrency: false,
        unit: 'clientes',
      },
    ]

    if (visao === 'ano' || visao === 'mes') {
      list.push({
        id: 'estoque',
        title: 'ESTOQUE',
        color: '#c9a100',
        chartColor: '#FFB020',
        rows: estoqueRows,
        isCurrency: false,
        unit: 'cx',
      })
    }

    return list
  }, [
    selloutRows,
    selloutValorRows,
    sellinValorRows,
    bonificacaoRows,
    coberturaRows,
    estoqueRows,
    visao,
  ])

  // Exportação para PDF
  const exportToPDF = async () => {
    setLoading(true)
    try {
      const pdf = new (jsPDF as any)('l', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      const minhaLogoBase64 = (await getBase64ImageFromUrl(GidIcon)) as string

      const originalAddPage = pdf.addPage.bind(pdf)
      pdf.addPage = function () {
        originalAddPage()
        pdf.setFillColor(0, 29, 83)
        pdf.rect(0, 0, pageWidth, pageHeight, 'F')
        return pdf
      }

      pdf.setFillColor(0, 29, 83)
      pdf.rect(0, 0, pageWidth, pageHeight, 'F')

      pdf.addImage(minhaLogoBase64, 'PNG', 14, 10, 30, 20)

      pdf.setTextColor(255, 255, 255)
      pdf.setFontSize(18)
      pdf.setFont('helvetica', 'bold')
      pdf.text('RELATÓRIO DE FASEAMENTO', pageWidth / 2, 20, { align: 'center' })

      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'normal')
      const periodoTexto =
        startDate && endDate
          ? `${startDate.toLocaleDateString()} até ${endDate.toLocaleDateString()}`
          : visao === 'semanas'
          ? selectedMonths.length > 1
            ? `Semanas${semana && semana !== 'todas' ? ` (${semana})` : ''} de ${monthNames[selectedMonths[0]]} até ${monthNames[selectedMonths[selectedMonths.length - 1]]} / ${year}`
            : `Semanas${semana && semana !== 'todas' ? ` (${semana})` : ''} de ${monthNames[selectedMonths[0] || activeMonthIdx]} / ${year}`
          : visao === 'trimestre'
          ? `Visão Trimestral (${year} vs ${targetCompare})`
          : visao === 'mes'
          ? `${monthNames[activeMonthIdx]} (${year}) vs ${monthNames[activeCompareMonthIdx]} (${targetCompare})`
          : `Ano ${year} vs ${targetCompare}${activeAnoMonths.length < 12 ? ` (${activeAnoMonths.map((m) => monthNames[m].slice(0, 3)).join(', ')})` : ''}`

      pdf.text(`Período: ${periodoTexto}`, pageWidth / 2, 28, { align: 'center' })

      if (lastUpdate) {
        pdf.text(getFormattedDate(), pageWidth / 2, 34, { align: 'center' })
      }

      pdf.setFillColor(166, 193, 11)
      pdf.rect(0, 40, pageWidth, 2, 'F')

      const headTitle = [
        'Métrica',
        ...tableColumns.map((c) => c.label),
        visao === 'semanas' ? (semana && semana !== 'todas' && selectedMonths.length === 1 ? `Total ${semana}` : 'Total Período') : 'Total',
      ]

      let currentY = 50

      const drawTable = (
        title: string,
        rows: any[],
        headerColor: [number, number, number],
        isCurrency: boolean = false,
      ) => {
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.setTextColor(255, 255, 255)
        pdf.text(title, 14, currentY)
        currentY += 5

        const body = rows.map((row) => {
          const isPerc = row.isPercentage
          const formatVal = (val: number) => {
            if (isPerc) return `${val > 0 ? '+' : ''}${val.toFixed(1)}%`
            if (isCurrency) {
              return val.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            }
            return Math.round(val).toLocaleString('pt-BR')
          }

          return [row.label, ...row.values.map(formatVal), formatVal(row.total)]
        })

        autoTable(pdf, {
          startY: currentY,
          head: [headTitle],
          body: body,
          headStyles: {
            fillColor: headerColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: tableColumns.length > 6 ? 7 : 9,
          },
          bodyStyles: {
            fillColor: [0, 29, 83],
            textColor: [255, 255, 255],
            lineColor: [20, 50, 110],
            lineWidth: 0.1,
          },
          columnStyles: {
            0: { halign: 'left', fontStyle: 'bold', cellWidth: tableColumns.length > 6 ? 26 : 35 },
          },
          alternateRowStyles: {
            fillColor: [0, 40, 100],
          },
          styles: {
            fontSize: tableColumns.length > 6 ? 7 : 9,
            cellPadding: tableColumns.length > 6 ? 1.5 : 3,
            halign: 'center',
            valign: 'middle',
          },
          didDrawPage: () => {
            pdf.saveGraphicsState()
            pdf.setGState(new pdf.GState({ opacity: 0.05 }))
            const watermarkWidth = 120
            const watermarkHeight = 120
            const centerX = (pageWidth - watermarkWidth) / 2
            const centerY = (pageHeight - watermarkHeight) / 2
            pdf.addImage(minhaLogoBase64, 'PNG', centerX, centerY, watermarkWidth, watermarkHeight)
            pdf.restoreGraphicsState()
          },
        })

        currentY = (pdf as any).lastAutoTable.finalY + 10
      }

      drawTable('REAL SELLOUT (CX)', selloutRows, [11, 107, 44])
      drawTable('REAL SELLOUT (R$)', selloutValorRows, [0, 80, 179], true)
      drawTable('REAL SELLIN (R$)', sellinValorRows, [3, 70, 104], true)
      drawTable('REAL BONIFICAÇÃO (R$)', bonificacaoRows, [114, 46, 209], true)
      drawTable('REAL COBERTURA', coberturaRows, [212, 107, 0])
      if (visao === 'ano' || visao === 'mes') {
        drawTable('ESTOQUE', estoqueRows, [201, 161, 0])
      }

      pdf.save(`Relatorio_Faseamento_${visao}_${year}.pdf`)
    } catch (error) {
      console.error('Erro ao gerar PDF', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <Skeleton variant='table' />

  const totalLabel =
    visao === 'semanas'
      ? selectedMonths.length > 1
        ? 'TOTAL PERÍODO'
        : semana && semana !== 'todas'
        ? `TOTAL ${semana}`
        : 'TOTAL MÊS'
      : visao === 'ano' && activeAnoMonths.length < 12
      ? 'TOTAL ACUMULADO'
      : 'TOTAL'

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0px 20px' }}>
        <button
          onClick={exportToPDF}
          style={{
            position: 'fixed',
            bottom: 80,
            padding: '10px 20px',
            backgroundColor: '#001D53',
            color: 'white',
            border: 'none',
            height: 60,
            borderRadius: 20,
            cursor: 'pointer',
            zIndex: 9999999,
            fontFamily: 'Montserrat',
            fontWeight: 'bold',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.2)',
          }}
          title='Exportar Relatório PDF'
        >
          <CiSaveDown1 style={{ color: '#ffffff', fontSize: 24 }} />
        </button>
      </div>

      <DashMain ref={dashRef} id='print-area'>
        {/* CABEÇALHO DO PAINEL */}
        <GeralTitleSection>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <h1>
                Faseamento
              </h1>

              {lastUpdate && (
                <span className='last-update'>
                  {getFormattedDate()}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <VisaoBadge>
                {visao === 'ano' &&
                  `Ano vs Ano (${year} vs ${targetCompare})${activeAnoMonths.length < 12 ? ` • ${activeAnoMonths.length} meses` : ''}`}
                {visao === 'trimestre' && `Trimestre vs Trimestre (${year} vs ${targetCompare})`}
                {visao === 'mes' &&
                  `Mês vs Mês: ${monthNames[activeMonthIdx]}/${year} vs ${monthNames[activeCompareMonthIdx]}/${targetCompare}`}
                {visao === 'semanas' &&
                  (selectedMonths.length > 1
                    ? `Semanas${semana && semana !== 'todas' ? ` (${semana})` : ''}: ${monthNames[selectedMonths[0]]} a ${monthNames[selectedMonths[selectedMonths.length - 1]]} / ${year}`
                    : `Semanas${semana && semana !== 'todas' ? ` (${semana})` : ''}: ${monthNames[selectedMonths[0] || activeMonthIdx]} / ${year}`)}
              </VisaoBadge>

              {distributorIds && distributorIds.length > 0 && (
                <DistFilterBadge>
                  {distributorIds.length} distribuidor(es) selecionado(s)
                </DistFilterBadge>
              )}
            </div>
          </div>

          <p className='periodo'>
            Período:{' '}
            <strong>
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} até ${endDate.toLocaleDateString()}`
                : visao === 'semanas'
                ? selectedMonths.length > 1
                  ? `Semanas${semana && semana !== 'todas' ? ` (${semana})` : ''} de ${monthNames[selectedMonths[0]]} até ${monthNames[selectedMonths[selectedMonths.length - 1]]} / ${year}`
                  : `Semanas${semana && semana !== 'todas' ? ` (${semana})` : ''} de ${monthNames[selectedMonths[0] || activeMonthIdx]} / ${year}`
                : visao === 'trimestre'
                ? `Visão Trimestral: ${year} vs ${targetCompare}`
                : visao === 'mes'
                ? `${monthNames[activeMonthIdx]}/${year} vs ${monthNames[activeCompareMonthIdx]}/${targetCompare}`
                : `Ano ${year} vs ${targetCompare}${activeAnoMonths.length < 12 ? ` (${activeAnoMonths.map((m) => monthNames[m].slice(0, 3)).join(', ')})` : ''}`}
            </strong>
          </p>
        </GeralTitleSection>

        {/* BARRA DE CONTROLE: FORMATO (TABELA PRIMEIRO / GRÁFICOS / AMBOS) */}
        <ControlsBar>
          <ViewToggleGroup>
            <ViewToggleButton
              active={viewFormat === 'tabelas'}
              onClick={() => setViewFormat('tabelas')}
              >
                <IoGridOutline size={16} /> Tabelas
              </ViewToggleButton>
              <ViewToggleButton
                active={viewFormat === 'graficos'}
                onClick={() => setViewFormat('graficos')}
              >
                <IoStatsChartOutline size={16} /> Gráficos
              </ViewToggleButton>
              <ViewToggleButton
                active={viewFormat === 'ambos'}
                onClick={() => setViewFormat('ambos')}
              >
                <IoLayersOutline size={16} /> Ambos
              </ViewToggleButton>
            </ViewToggleGroup>

            <span className='metric-count'>
              Exibindo <strong>{allMetrics.length}</strong> métricas
            </span>
          </ControlsBar>

        {/* AVISO QUANDO NENHUM MÊS ESTIVER SELECIONADO EM ANO VS ANO */}
        {visao === 'ano' && activeAnoMonths.length === 0 && (
          <div
            style={{
              padding: '16px 20px',
              backgroundColor: 'rgba(255, 77, 79, 0.12)',
              border: '1px solid #ff4d4f',
              borderRadius: '10px',
              color: '#ffffff',
              marginBottom: '20px',
              textAlign: 'center',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Nenhum mês selecionado no filtro de Ano vs Ano. Abra o menu de filtros e selecione os meses desejados.
          </div>
        )}

        {/* CONTEÚDO: TODAS AS MÉTRICAS NA MESMA TELA */}
        <div style={{ padding: '0px', borderRadius: '12px' }}>
          {allMetrics.map((metric) => (
            <MetricBlock key={metric.id}>
              {/* Gráfico */}
              {(viewFormat === 'graficos' || viewFormat === 'ambos') && (
                <GeralChartCard
                  title={metric.title}
                  color={metric.chartColor}
                  columns={tableColumns}
                  rows={metric.rows}
                  isCurrency={metric.isCurrency}
                  unit={metric.unit}
                />
              )}

              {/* Tabela */}
              {(viewFormat === 'tabelas' || viewFormat === 'ambos') && (
                <Table
                  title={metric.title}
                  color={metric.color}
                  monthLabel={monthNames[activeMonthIdx]}
                  showAllMonths={visao === 'ano'}
                  customColumns={tableColumns}
                  totalLabel={totalLabel}
                  rows={metric.rows}
                />
              )}
            </MetricBlock>
          ))}
        </div>
      </DashMain>
    </>
  )
}

// --- ESTILOS ---

const VisaoBadge = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? 'rgba(2, 132, 199, 0.12)' : 'rgba(0, 80, 179, 0.25)')};
  color: ${(props) => (props.theme.name === 'light' ? '#0369a1' : '#69c0ff')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? 'rgba(2, 132, 199, 0.35)' : 'rgba(105, 192, 255, 0.4)')};
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
`

const DistFilterBadge = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? 'rgba(101, 163, 13, 0.12)' : 'rgba(166, 193, 11, 0.15)')};
  color: ${(props) => (props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? 'rgba(101, 163, 13, 0.35)' : 'rgba(166, 193, 11, 0.35)')};
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
`

const ControlsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 8px 14px;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 20, 60, 0.5)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? props.theme.cardBorder : 'rgba(255, 255, 255, 0.08)')};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.04)' : 'none')};
  border-radius: 12px;

  .metric-count {
    font-size: 12px;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#a0aec0')};
    font-family: 'Montserrat', sans-serif;
    strong {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }
  }
`

const ViewToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 0, 0, 0.3)')};
  padding: 4px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.06)')};
`

const ViewToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: ${(props) => (props.active ? '#a6c10b' : 'transparent')};
  color: ${(props) => (props.active ? '#001D53' : props.theme.name === 'light' ? '#475569' : '#e0e0e0')};
  font-size: 12px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.active ? '#a6c10b' : props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    color: ${(props) => (props.active ? '#001D53' : props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  }
`

const MetricBlock = styled.div`
  margin-bottom: 20px;
`

const GeralTitleSection = styled.div`
  border-left: 5px solid #a6c10b;
  padding-left: 15px;
  margin-bottom: 15px;

  h1 {
    font-size: 20px;
    color: ${(props) => props.theme.titleColor};
    margin: 0;
    font-weight: bold;
  }

  .last-update {
    font-size: 12px;
    color: ${(props) => props.theme.textSecondary};
    font-style: italic;
  }

  p.periodo {
    font-size: 14px;
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#a6c10b')};
    margin-top: 5px;
    margin-bottom: 12px;

    strong {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }
  }
`
